import { Empty, FillRow, HitRow, rankOf } from "@/components/find/HitRow";
import {
  fitFromReceipt, formatGal, inchesFromTankVolume, rankTanks, rankTanksByDrop,
  rankTanksByUllage, tanksFromMaxFill,
} from "@/lib/tank";
import { useT } from "@/lib/i18n";

const pct = (x) => `(${x.deltaPct.toFixed(1)}%)`;

const hitProps = (x, i, detail, onUse) => ({
  tank: x, mfg: x.mfg, sti: x.sti, lengthLabel: x.lengthLabel, rank: rankOf(i, x.deltaPct), detail, onUse,
});

function List({ title, children }) {
  return (<><div className="results-title">{title}</div><div className="list" data-testid="results-list">{children}</div></>);
}

function LevelResults({ s, use, t }) {
  const h = s.solverInches, g = s.solverGallons;
  if (h <= 0 || g <= 0) return <Empty>{t("Enter the stick you pulled and the gallons the console claims.")}</Empty>;
  const hits = rankTanks(h, g, s.solverCap);
  if (!hits.length) return <Empty danger>{t("No standard tank is deeper than {h}\". Try any capacity.", { h })}</Empty>;
  return (
    <List title={t("Closest tanks")}>
      {hits.slice(0, 8).map((x, i) => (
        <HitRow key={i} {...hitProps(x, i,
          t("Model at {h}\" → {v} gal · off {d} {p} · {f}% full", { h, v: formatGal(x.calcVol), d: `${x.calcVol >= g ? "+" : "−"}${formatGal(x.delta)}`, p: pct(x), f: x.fillPct.toFixed(1) }),
          () => use(x, h))} />
      ))}
    </List>
  );
}

function UllageResults({ s, use, t }) {
  const h = s.solverInches, u = s.solverGallons;
  if (h <= 0 || u <= 0) return <Empty>{t("Enter the stick and the ullage gallons.")}</Empty>;
  const hits = rankTanksByUllage(h, u, s.solverUllageTo);
  if (!hits.length) return <Empty danger>{t("No standard tank is deeper than {h}\".", { h })}</Empty>;
  const to = t(s.solverUllageTo === "90" ? "to 90%" : "to full");
  return (
    <List title={t("Closest tanks")}>
      {hits.slice(0, 6).map((x, i) => (
        <HitRow key={i} {...hitProps(x, i, t("Room {v} gal {to} · off {d} {p}", { v: formatGal(x.calcVol), to, d: formatGal(x.delta), p: pct(x) }), () => use(x, h))} />
      ))}
    </List>
  );
}

function DropResults({ s, use, t }) {
  const { solverStart: a, solverEnd: b, solverDelivered: d } = s;
  if (d <= 0 || b <= 0) return <Empty>{t("Enter the starting stick, the ending stick and the gallons on the meter.")}</Empty>;
  if (b <= a) return <Empty danger>{t("The ending stick has to be higher than the start.")}</Empty>;
  const hits = rankTanksByDrop(a, b, d);
  if (!hits.length) return <Empty danger>{t("No standard tank is deep enough for a {b}\" ending stick.", { b })}</Empty>;
  return (
    <List title={t("Closest tanks")}>
      {hits.slice(0, 6).map((x, i) => (
        <HitRow key={i} {...hitProps(x, i, t("Rise {v} gal · off {d} {p}", { v: formatGal(x.riseGallons), d: formatGal(x.delta), p: pct(x) }), () => use(x, a, d))} />
      ))}
    </List>
  );
}

function LineResults({ s, use, t }) {
  if (s.solverFillLine <= 0) return <Empty>{t("Enter the max fill in gallons, plus the ullage up to that fill.")}</Empty>;
  const r = tanksFromMaxFill(s.solverFillLine, s.solverFillPct, s.solverGallons);
  return (
    <>
      <div className="highlight" data-testid="line-result">
        <div className="results-title">{t("Fuel in the tank")}</div>
        <div className="highlight-num">{r.fuelGallons < 0 ? "—" : `${formatGal(r.fuelGallons)} gal`}</div>
        <div className="rcpt-line">{t("{g} gal at {p}% is a {c} gal tank.", { g: formatGal(s.solverFillLine), p: s.solverFillPct, c: formatGal(r.capacity) })}</div>
      </div>
      <List title={t("Your tank")}>{r.hits.map((x, i) => <FillRow key={i} item={x} onUse={() => use(x, x.fuelInches)} />)}</List>
    </>
  );
}

function VolumeResults({ s, use, t }) {
  if (s.solverTankGallons <= 0 || s.solverGallons <= 0) return <Empty>{t("Enter the gallons in the tank and the size on the tag.")}</Empty>;
  const r = inchesFromTankVolume(s.solverGallons, s.solverTankGallons, s.solverTankPct);
  return (
    <>
      <div className="rcpt-line">{t("{g} gal at {p}% is a {c} gal tank.", { g: formatGal(s.solverTankGallons), p: s.solverTankPct, c: formatGal(r.capacity) })}</div>
      <List title={t("Stick inches")}>{r.hits.map((x, i) => <FillRow key={i} big item={x} onUse={() => use(x, x.fuelInches)} />)}</List>
    </>
  );
}

function ReceiptResults({ s, use, t }) {
  const r = fitFromReceipt(s.rcptHeight, s.rcptVolume, s.rcptUllage, s.rcptUllageKind);
  if (!r) return <Empty>{t("Enter the height, the volume and the ullage from the printout.")}</Empty>;
  const h = s.rcptHeight;
  const dia = (d) => (d == null ? "—" : t("{d}\" wide", { d: d.toFixed(1) }));
  const custom = r.flatDiameter && { capacity: r.capacity, diameter: Math.round(r.flatDiameter * 10) / 10, ends: "flat", deltaPct: 0 };
  return (
    <>
      <div className="highlight" data-testid="rcpt-result">
        <div className="results-title">{t("Tank size")}</div>
        <div className="highlight-num" data-testid="rcpt-capacity">{formatGal(r.capacity)} gal</div>
        <div className="rcpt-line" data-testid="rcpt-diameter-flat">{dia(r.flatDiameter)} {t("if flat ends (steel)")}</div>
        <div className="rcpt-line" data-testid="rcpt-diameter-dome">{dia(r.domeDiameter)} {t("if rounded ends (fiberglass)")}</div>
      </div>
      <List title={t("Closest tanks")}>
        {r.hits.slice(0, 6).map((x, i) => (
          <HitRow key={i} {...hitProps(x, i, t("Chart at {h}\" → {v} gal · off {d} {p}", { h, v: formatGal(x.calcVol), d: formatGal(x.delta), p: pct(x) }), () => use(x, h))} />
        ))}
        {!r.hits.length && custom && (
          <HitRow tank={custom} mfg={t("Worked out from your printout")} rank="best"
            detail={t("Fits the printout exactly · {d}\" flat ends", { d: r.flatDiameter.toFixed(1) })} onUse={() => use(custom, h)} />
        )}
      </List>
    </>
  );
}

const MAP = { level: LevelResults, ullage: UllageResults, drop: DropResults, line: LineResults, volume: VolumeResults, receipt: ReceiptResults };

export function Results({ s, use }) {
  const t = useT();
  const C = MAP[s.solverMode];
  const many = ["level", "ullage", "drop"].includes(s.solverMode);
  return (
    <div className="results" data-testid={`results-${s.solverMode}`}>
      {many && <div className="many-note" data-testid="results-many-note">{t("These are possible matches: more than one tank size can fit the same numbers. Pick the one at this station, save it, then use Stick only for one exact answer.")}</div>}
      <C s={s} use={use} t={t} />
    </div>
  );
}
